import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Users,
  Sparkles,
  Megaphone,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Phone,
  Calendar,
  Database,
  TrendingUp,
  Globe2,
  MessagesSquare,
} from "lucide-react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

// People view — Smartorange manager profiles, mirrors the design the user
// requested (avatar / Overall Score / CRM + Calls + Meetings breakdown).
// Backed by /marketing-analytics/manager-profiles. No new LLM calls — all
// aggregation comes from existing lead summaries.

function calendarMonthRange(anchorMonthsAgo = 1) {
  const now = new Date();
  const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - anchorMonthsAgo, 1));
  const first = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), 1));
  let last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0));
  if (anchorMonthsAgo === 0 && last > now) last = new Date(now);
  const label = first.toLocaleString("en-GB", {
    month: "short", year: "numeric", timeZone: "UTC",
  });
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10), label };
}

function _initials(name) {
  if (!name) return "?";
  const clean = String(name).replace(/Landing Lead — /i, "").replace(/\[FORM_LEAD\]/, "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

function _avatarBg(name) {
  const palette = [
    "bg-emerald-500/30 text-emerald-100",
    "bg-cyan-500/30 text-cyan-100",
    "bg-purple-500/30 text-purple-100",
    "bg-amber-500/30 text-amber-100",
    "bg-pink-500/30 text-pink-100",
    "bg-blue-500/30 text-blue-100",
  ];
  let h = 0;
  for (let i = 0; i < String(name || "").length; i++) h = (h * 31 + String(name).charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

// Score color in the "Cody Fisher 43 / Whitesales 60 / Profile 93" style —
// big number coloured red ≤49, amber 50-69, emerald 70+.
function scoreColor(n) {
  if (n == null) return "text-zinc-500";
  if (n >= 70) return "text-emerald-300";
  if (n >= 50) return "text-amber-300";
  return "text-red-300";
}

// Render "+12" / "-4" delta chip next to a score. Positive = green,
// negative = red, zero = zinc, null = nothing rendered.
function DeltaChip({ delta, size = "sm" }) {
  if (delta == null) return null;
  const cls = delta > 0 ? "text-emerald-400" : delta < 0 ? "text-red-400" : "text-zinc-500";
  const sign = delta > 0 ? "+" : "";
  const sz = size === "sm" ? "text-[10px]" : "text-xs";
  return <sup className={`${cls} ${sz} font-semibold ml-0.5`}>{sign}{delta}</sup>;
}

function PersonCard({ p }) {
  const overall = p.overall_score;
  return (
    <Link
      to={`/marketing-analytics/people/${encodeURIComponent(p.owner_id)}`}
      className="block"
    >
      <Card className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className={`shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-base font-semibold ${_avatarBg(p.owner_name)}`}>
              {_initials(p.owner_name)}
            </div>
            <div className="text-right">
              <div className={`text-4xl font-bold ${scoreColor(overall)}`}>
                {overall ?? "—"}
                <DeltaChip delta={p.overall_score_delta} />
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wider">
                Overall Score
              </div>
            </div>
          </div>
          <div className="mt-3">
            <div className="text-zinc-100 font-semibold text-lg truncate">{p.owner_name}</div>
            <div className="text-zinc-500 text-xs">
              Sales · Seller
              {(() => {
                const total = p.total_lead_count ?? p.lead_count ?? 0;
                const graded = p.lead_count ?? 0;
                if (total === 0) return null;
                if (graded < total) {
                  return (
                    <> · <span className="text-zinc-300">{graded}</span> of <span className="text-zinc-300">{total}</span> leads graded</>
                  );
                }
                return <> · {graded} lead{graded === 1 ? "" : "s"}</>;
              })()}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
            {["crm", "calls", "meetings"].map((bk) => {
              const b = p.buckets?.[bk];
              const avg = b?.average;
              return (
                <div key={bk}>
                  <div className="text-zinc-500 inline-flex items-center gap-1 uppercase tracking-wider text-[10px]">
                    {bk === "crm" ? <Database className="w-2.5 h-2.5" /> : bk === "calls" ? <Phone className="w-2.5 h-2.5" /> : <Calendar className="w-2.5 h-2.5" />}
                    {b?.label || bk}
                  </div>
                  <div className={`text-2xl font-bold ${scoreColor(avg)} mt-0.5`}>
                    {avg ?? "—"}
                    <DeltaChip delta={b?.average_delta} />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function MarketingPeoplePage() {
  const [searchParams] = useSearchParams();
  const initFrom = searchParams.get("from");
  const initTo = searchParams.get("to");
  // Default to CURRENT month per user request — "show me what's happening
  // right now in May, not last month's April". Partial month is fine,
  // last-month is always one click away.
  const thisMonth = calendarMonthRange(0);
  const [dateFrom, setDateFrom] = useState(initFrom || thisMonth.from);
  const [dateTo, setDateTo] = useState(initTo || thisMonth.to);
  const [monthAnchor, setMonthAnchor] = useState(initFrom ? null : 0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("overall");

  const fetchData = async () => {
    setLoading(true); setError("");
    try {
      const res = await api.get("/marketing-analytics/manager-profiles", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  // Apply sort
  const sortedManagers = (() => {
    const list = [...(data?.managers || [])];
    const _v = (m, key) => {
      if (key === "overall") return m.overall_score ?? -1;
      return m.buckets?.[key]?.average ?? -1;
    };
    list.sort((a, b) => _v(b, sortKey) - _v(a, sortKey));
    return list;
  })();

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-1 border-b border-zinc-800 pb-0.5">
        <Link to="/marketing-analytics" className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition">Overview</Link>
        <Link to="/marketing-analytics/people" className="px-4 py-2 text-sm text-blue-400 font-medium border-b-2 border-blue-500 -mb-0.5 inline-flex items-center gap-1">
          <Users className="w-3 h-3" /> People
        </Link>
        <Link to={`/marketing-analytics/sales?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> Sales scoring
        </Link>
        <Link to={`/marketing-analytics/landings?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <Globe2 className="w-3 h-3" /> Landings &amp; Leads
        </Link>
        <Link to={`/marketing-analytics/chats?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <MessagesSquare className="w-3 h-3" /> Chats
        </Link>
      </div>

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-400" /> People
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Manager skill profiles built from AI-graded lead conversations. Click a card for the full breakdown.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <span className="uppercase tracking-wider">Sort by</span>
          {[
            { key: "overall",  label: "Overall" },
            { key: "crm",      label: "CRM" },
            { key: "calls",    label: "Calls" },
            { key: "meetings", label: "Meetings" },
          ].map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSortKey(s.key)}
              className={`px-2 py-1 rounded border ${
                sortKey === s.key
                  ? "bg-blue-500/20 text-blue-200 border-blue-500/40"
                  : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap text-xs bg-zinc-900/40 border border-zinc-800 rounded-lg px-3 py-2">
        <span className="text-zinc-500 uppercase tracking-wider">Period</span>
        {[{ months: 0, label: "This month" }, { months: 1, label: "Last month" }].map((p) => {
          const r = calendarMonthRange(p.months);
          const active = monthAnchor === p.months;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => { setMonthAnchor(p.months); setDateFrom(r.from); setDateTo(r.to); }}
              className={`px-2.5 py-1 rounded border transition ${
                active ? "bg-blue-500/20 text-blue-200 border-blue-500/40" : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
              }`}
            >
              {p.label}
            </button>
          );
        })}
        <div className="inline-flex items-center bg-zinc-900/60 border border-zinc-800 rounded">
          <button type="button"
            onClick={() => {
              const next = (monthAnchor == null ? 1 : monthAnchor) + 1;
              const r = calendarMonthRange(next);
              setMonthAnchor(next); setDateFrom(r.from); setDateTo(r.to);
            }}
            className="px-2 py-1 text-zinc-400 hover:text-zinc-100 border-r border-zinc-800"
          >
            <ChevronRight className="w-3 h-3 rotate-180" />
          </button>
          <span className="px-2 py-1 text-zinc-300 min-w-[110px] text-center">
            {monthAnchor != null && monthAnchor >= 0 ? calendarMonthRange(monthAnchor).label : "Custom range"}
          </span>
          <button type="button"
            onClick={() => {
              const next = Math.max(0, (monthAnchor == null ? 1 : monthAnchor) - 1);
              const r = calendarMonthRange(next);
              setMonthAnchor(next); setDateFrom(r.from); setDateTo(r.to);
            }}
            disabled={monthAnchor === 0}
            className="px-2 py-1 text-zinc-400 hover:text-zinc-100 disabled:opacity-30 border-l border-zinc-800"
          >
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1.5 text-zinc-400">
          <Input type="date" value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setMonthAnchor(null); }}
            className="bg-zinc-900/60 border-zinc-800 text-xs h-7 w-36 text-zinc-200"
          />
          <span className="text-zinc-600">→</span>
          <Input type="date" value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setMonthAnchor(null); }}
            className="bg-zinc-900/60 border-zinc-800 text-xs h-7 w-36 text-zinc-200"
          />
        </div>
      </div>

      {error && (
        <Card className="bg-red-900/20 border-red-800">
          <CardContent className="py-3 text-red-200 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {error}
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-8 text-center text-zinc-400 text-sm inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading profiles…
          </CardContent>
        </Card>
      )}

      {data && (sortedManagers.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-8 text-center text-zinc-500 text-sm">
            No analyzed leads attributed to a manager in this period yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {sortedManagers.map((p) => (
            <PersonCard key={p.owner_id} p={p} />
          ))}
        </div>
      ))}
    </div>
  );
}
